import { Box, Button } from "@mui/material";
import { disabledBlockSx } from "../disabledStyles";
import { ContentGalleryModal } from "@platformx/authoring-utilities";
import { ShowToastError } from "@platformx/utilities";
import { memo, useEffect, useState } from "react";
import { fetchSchemaWithData } from "../../../utils/helperFns";
import { getRepeaterValue, setRepeaterValue } from "../../../utils/repeaterPath";
import { DraggableItems } from "../DragableItem/DragableItems";
import HiddenTextBox from "../HiddenTextBox/HiddenTextBox";

// When rendered inside a (possibly nested) repeater, field.name is a synthetic
// RHF path (e.g. `entries[0].foo_entries_123`), not a real state key — reads and
// writes must go through field.repeaterPath/childName instead, mirroring the
// coordinate pattern builder.tsx's own handleChange/updateState helpers use.
const getContentValue = (state: any, field: any, isRepeater: boolean) =>
  isRepeater
    ? getRepeaterValue(state, field?.repeaterPath, field?.childName ?? "")
    : state?.[field?.name];

const setContentValue = (
  state: any,
  setState: any,
  field: any,
  isRepeater: boolean,
  value: any,
) => {
  if (isRepeater) {
    setState(setRepeaterValue(state, field?.repeaterPath, field?.childName ?? "", value));
    return;
  }
  setState({
    ...state,
    [field?.name]: value,
  });
};

const AddContent = ({
  state,
  setState,
  field,
  error,
  register,
  clearErrors,
  isRepeater = false,
  isDisabled = false,
}: any) => {
  const [isOpenContent, setIsOpenContent] = useState(false);
  interface PopulatedItem {
    title: string;
    name: string;
    tag_name: string;
    current_page_url: string;
  }

  const [populatedItems, setPopulatedItems] = useState<PopulatedItem[]>([]);
  const contentValue = getContentValue(state, field, isRepeater);

  const handleGalleryClose = () => {
    setIsOpenContent(false);
  };

  const handleSelectedContent = (items) => {
    const newArray = items?.slots?.map((val) => {
      return {
        Title: val.Title,
        content_path: val.path || val.Path || val.content_path || val.CurrentPageURL || "",
        content_name: val.page || val.content_name || val.Title || "",
        CurrentPageURL: val.slug || val.Slug || val.CurrentPageURL || "",
        content_type: val.ContentType || val.content_type,
        is_hidden: false,
        type: "content",
      };
    });
    setContentValue(state, setState, field, isRepeater, newArray);
  };

  //GetSchemaData API
  const getSchema = async (Items: any) => {
    try {
      const promises = Items?.map((item) => {
        return fetchSchemaWithData(item.content_type, item.content_name, item.type);
      });
      const outputs: any = await Promise.allSettled(promises);
      const tempArray: PopulatedItem[] = [];
      outputs?.forEach((output, index) => {
        if (output?.status === "rejected") {
          console.error("Error in fetching row items data", output);
          // Fallback: use the stored Title from state so the item still shows
          const fallbackItem = Items?.[index];
          if (fallbackItem?.Title) {
            tempArray.push({
              title: fallbackItem.Title,
              name: fallbackItem.content_name || fallbackItem.Title,
              tag_name: fallbackItem.content_type || "",
              current_page_url: fallbackItem.CurrentPageURL || fallbackItem.content_path || "",
            });
          }
        } else if (output?.status === "fulfilled") {
          const schema = output?.value?.authoring_getSchemaContent || {};
          if (schema && schema.title) {
            tempArray.push({
              title: schema?.title,
              name: schema?.name,
              tag_name: schema?.tag_name,
              current_page_url: schema?.current_page_url,
            });
          } else {
            // API returned but no schema data — fallback to stored values
            const fallbackItem = Items?.[index];
            if (fallbackItem?.Title) {
              tempArray.push({
                title: fallbackItem.Title,
                name: fallbackItem.content_name || fallbackItem.Title,
                tag_name: fallbackItem.content_type || "",
                current_page_url: fallbackItem.CurrentPageURL || fallbackItem.content_path || "",
              });
            }
          }
        }
      });
      setPopulatedItems(tempArray);
    } catch (err: any) {
      ShowToastError("Error in fetching row items data");
    }
  };

  useEffect(() => {
    clearErrors(field?.name);
    if (contentValue?.length > 0) {
      getSchema(contentValue);
    } else if (contentValue?.length === 0) {
      setPopulatedItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentValue]);

  // Adapter so the shared DraggableItems component (which reads/writes `state[name]`
  // directly as a flat array) can drive the repeater-nested value here.
  const draggableAdapterState = { [field?.name]: contentValue || [] };
  const draggableAdapterSetState = (adapterState: any) => {
    setContentValue(state, setState, field, isRepeater, adapterState?.[field?.name] ?? []);
  };

  return (
    <Box id={`${field?.name}_id`} sx={disabledBlockSx(isDisabled)}>
      <Box
        sx={{
          ".draggable-category": {
            paddingRight: "0",
          },
        }}>
        <DraggableItems
          state={draggableAdapterState}
          setState={draggableAdapterSetState}
          name={field?.name}
          populatedItems={populatedItems}
        />
      </Box>
      <Button variant='primaryButton' onClick={() => setIsOpenContent(true)}>
        {field?.title || "Choose Content"}
      </Button>
      {isOpenContent ? (
        <ContentGalleryModal
          isDialogOpen={isOpenContent}
          onClose={handleGalleryClose}
          handleSelectedContent={handleSelectedContent}
          selectedItem={contentValue || []}
          multi_select={field?.multiple}
          single_select={!field?.multiple}
          name={field?.name}
          allowed_content={field?.allowed_content}
          allowed_collection={field?.allowed_collection}
        />
      ) : null}
      <HiddenTextBox
        error={error}
        value={contentValue?.length || ""}
        register={register}
        name={field?.name}
        required={!contentValue?.length ? field?.validations?.[0]?.message : ""}
      />
    </Box>
  );
};

export default memo(AddContent);
